/**
 * Role Type Definitions
 * Sprint 2.2+: ABP Permissions Migration
 *
 * IMPORTANT: This file now only contains role definitions.
 * Permissions are managed by ABP and fetched from /api/abp/application-configuration.
 * See src/constants/permissions.ts for ABP permission constants.
 *
 * Roles Hierarchy (from highest to lowest privileges):
 * 1. admin - Top-level administrator with full permissions
 * 2. opsAdmin - Operations admin (book/publisher/license management)
 * 3. publisher - Publisher role
 * 4. bookseller - Bookseller role
 * 5. professor - Professor role
 * 6. student - Student role
 * 7. supportStaff - Application support staff
 */

/**
 * User roles in the LearnLeaf platform
 */
export enum UserRole {
  ADMIN = 'admin',
  OPS_ADMIN = 'opsAdmin',
  PUBLISHER = 'publisher',
  BOOKSELLER = 'bookseller',
  PROFESSOR = 'professor',
  STUDENT = 'student',
  SUPPORT_STAFF = 'supportStaff',
}

/**
 * Role display names for UI
 */
export const RoleDisplayNames: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Administrator',
  [UserRole.OPS_ADMIN]: 'Operations Admin',
  [UserRole.PUBLISHER]: 'Publisher',
  [UserRole.BOOKSELLER]: 'Bookseller',
  [UserRole.PROFESSOR]: 'Professor',
  [UserRole.STUDENT]: 'Student',
  [UserRole.SUPPORT_STAFF]: 'Support Staff',
};

/**
 * @deprecated This enum is deprecated. Use ABP permissions from src/constants/permissions.ts instead.
 * ABP permissions are fetched dynamically from /api/abp/application-configuration.
 * This enum is kept for backwards compatibility only.
 */
export enum Permission {
  // DEPRECATED - Use AbpPermissions.ReadBook instead
  READ_BOOK = 'read:book',
}

/**
 * Role hierarchy levels (for comparison)
 */
export const RoleHierarchy: Record<UserRole, number> = {
  [UserRole.ADMIN]: 7,
  [UserRole.OPS_ADMIN]: 6,
  [UserRole.PUBLISHER]: 5,
  [UserRole.BOOKSELLER]: 4,
  [UserRole.PROFESSOR]: 3,
  [UserRole.STUDENT]: 2,
  [UserRole.SUPPORT_STAFF]: 1,
};

/**
 * @deprecated This object is deprecated. Permissions are now managed dynamically via ABP.
 * Permissions are assigned to roles in the Angular admin UI and fetched via /api/abp/application-configuration.
 * This object is kept for backwards compatibility only.
 */
export const RolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [Permission.READ_BOOK],
  [UserRole.OPS_ADMIN]: [Permission.READ_BOOK],
  [UserRole.PUBLISHER]: [Permission.READ_BOOK],
  [UserRole.BOOKSELLER]: [Permission.READ_BOOK],
  [UserRole.PROFESSOR]: [Permission.READ_BOOK],
  [UserRole.STUDENT]: [Permission.READ_BOOK],
  [UserRole.SUPPORT_STAFF]: [Permission.READ_BOOK],
};

/**
 * User with role information from OIDC
 */
export interface UserWithRoles {
  sub: string;
  name?: string;
  email?: string;
  roles: UserRole[];
  [key: string]: unknown;
}
